import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Activity, CheckCircle2, AlertCircle, XCircle, Clock, Circle, RefreshCw,
} from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

interface ConnectorHealth {
  id: number
  kind: string
  name: string
  vendor: string
  category: string
  enabled: boolean
  state: 'ok' | 'stale' | 'error' | 'never' | 'disabled'
  last_status: string
  last_run_at: string | null
  age_minutes: number | null
  last_duration_ms: number
  last_error: string
}

const STATE_STYLES: Record<string, { bg: string; text: string; border: string; label: string; icon: React.ElementType }> = {
  ok:       { bg: 'bg-emerald/10', text: 'text-emerald',     border: 'border-emerald/30', label: 'Healthy',   icon: CheckCircle2 },
  stale:    { bg: 'bg-amber/10',   text: 'text-amber-light', border: 'border-amber/30',   label: 'Stale',     icon: Clock },
  error:    { bg: 'bg-rose/10',    text: 'text-rose-light',  border: 'border-rose/30',    label: 'Error',     icon: XCircle },
  never:    { bg: 'bg-blue/10',    text: 'text-blue-light',  border: 'border-blue/30',    label: 'Never run', icon: Circle },
  disabled: { bg: 'bg-surface-hover', text: 'text-text-muted', border: 'border-border',   label: 'Disabled',  icon: Circle },
}

function relative(ts: string | null): string {
  if (!ts) return 'never'
  try { return formatDistanceToNow(new Date(ts), { addSuffix: true }) } catch { return ts }
}

export default function ConnectorHealthPage() {
  const [data, setData] = useState<{
    total: number
    by_state: Record<string, number>
    connectors: ConnectorHealth[]
    stale_after_minutes: number
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const [stateFilter, setStateFilter] = useState<'all' | 'ok' | 'stale' | 'error' | 'never' | 'disabled'>('all')

  const fetchHealth = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get('/api/connectors/health')
      setData(res.data)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchHealth(); const i = setInterval(fetchHealth, 60_000); return () => clearInterval(i) }, [fetchHealth])

  const filtered = (data?.connectors || []).filter(c => stateFilter === 'all' || c.state === stateFilter)

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Activity size={24} className="text-blue" /> Connector Health
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Last-run status, freshness and errors across every configured connector.
          </p>
        </div>
        <button type="button" onClick={fetchHealth} className="btn-secondary text-xs">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {(['ok', 'stale', 'error', 'never', 'disabled'] as const).map(state => {
            const s = STATE_STYLES[state]
            const Icon = s.icon
            const n = data.by_state[state] || 0
            const active = stateFilter === state
            return (
              <button
                key={state}
                type="button"
                onClick={() => setStateFilter(active ? 'all' : state)}
                className={clsx(
                  'card p-4 text-left transition-all border',
                  active ? s.border : 'border-border hover:border-border-light',
                  active && s.bg,
                )}
              >
                <div className="flex items-center gap-2">
                  <Icon size={16} className={s.text} />
                  <span className="text-xs text-text-secondary">{s.label}</span>
                </div>
                <div className={clsx('text-2xl font-bold mt-1', active ? s.text : 'text-text-primary')}>{n}</div>
              </button>
            )
          })}
        </div>
      )}

      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-text-primary">
            {stateFilter === 'all' ? 'All connectors' : `${STATE_STYLES[stateFilter].label} connectors`}
            <span className="text-[11px] text-text-muted font-normal ml-2">{filtered.length} of {data?.total ?? 0}</span>
          </h2>
          <span className="text-[10px] text-text-muted">
            stale &gt; {data ? Math.round(data.stale_after_minutes / 60) : '?'}h since last run
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wider text-text-muted">
              <tr className="border-b border-border">
                <th className="text-left py-2 px-2">State</th>
                <th className="text-left py-2 px-2">Connector</th>
                <th className="text-left py-2 px-2">Category</th>
                <th className="text-left py-2 px-2">Last run</th>
                <th className="text-right py-2 px-2">Duration</th>
                <th className="text-left py-2 px-2">Error</th>
                <th className="text-right py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const s = STATE_STYLES[c.state]
                const Icon = s.icon
                return (
                  <tr key={c.id} className="border-b border-border/50 hover:bg-surface-hover">
                    <td className="py-2 px-2">
                      <span className={clsx('inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[10px] border', s.border, s.bg, s.text)}>
                        <Icon size={10} /> {s.label}
                      </span>
                    </td>
                    <td className="py-2 px-2">
                      <div className="font-medium text-text-primary">{c.name}</div>
                      <div className="text-[10px] text-text-muted font-mono">{c.kind}</div>
                    </td>
                    <td className="py-2 px-2 text-text-secondary">{c.category}</td>
                    <td className="py-2 px-2 text-text-secondary">
                      {relative(c.last_run_at)}
                      {c.age_minutes !== null && c.age_minutes > (data?.stale_after_minutes || 360) && (
                        <AlertCircle size={10} className="inline ml-1 text-amber" />
                      )}
                    </td>
                    <td className="py-2 px-2 text-right text-text-muted font-mono">
                      {c.last_duration_ms ? `${c.last_duration_ms}ms` : '—'}
                    </td>
                    <td className="py-2 px-2 text-rose-light max-w-xs truncate" title={c.last_error}>
                      {c.last_error || '—'}
                    </td>
                    <td className="py-2 px-2 text-right">
                      <Link to={`/app/connectors-manage?edit=${c.id}`} className="text-blue-light text-[10px] hover:underline">
                        edit
                      </Link>
                    </td>
                  </tr>
                )
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="py-6 text-center text-text-muted">No connectors in this state.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
